import {Box, Button} from "@mui/material";
import './styles/task.scss'
import {
    analysisTaskProcess,
    AnalysisTaskSubProcess, CommandType,
    ConversionType, currentTaskCacheProps,
    ExtraProps,
    ResponseProp,
    TaskStatusType
} from "../../types/taskFlow.types";
import {useSocketCommands} from "../../hooks/useSocketCommands";
import {getLocalConversionId, getLocalStorage} from "../../util/storage";
import {useTaskFlow} from "../../context/TaskFlowContext";
import React, {Fragment, useCallback, useEffect, useRef} from "react";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import {setCurrentTaskFrameIds, setScreenPosition} from "../../store/drawSlice";
import {useDispatch, useSelector} from "react-redux";
import {useCanvasControl} from "../../hooks/useCanvasControl";
import CheckIcon from "@mui/icons-material/Check";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import {GradientCircularProgress, TaskLoading} from "../../icons";
import { debounce } from 'lodash';
import {RootState} from "../../store";
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom';


const TaskPlan = React.memo((props: { plan: analysisTaskProcess, status?: TaskStatusType, taskId?: number, extra?: ExtraProps, centerChat?: boolean, content?: string, currentTaskCache?: (value: currentTaskCacheProps) => void }) => {
    const {plan, status, taskId, extra, centerChat, content, currentTaskCache} = props;
    const { t } = useTranslation()
    const {executeTask} = useSocketCommands();
    const {taskProcess, taskListProcess, editor, changeTaskProcess, conversionValue, updateStatus} = useTaskFlow()
    const parentStepId = useSelector((state: RootState) => state.draw.currentStepId)
    const dispatch = useDispatch();

    const {  updateMainContainerStatus } = useCanvasControl(editor);

    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const boardId = queryParams.get('boardId');

    const hasExecuted = useRef(false);

    const excuseTask = useCallback(
        debounce((task: AnalysisTaskSubProcess, psId: string) => {
            if (hasExecuted.current) return;
            hasExecuted.current = true;
            const conversionId = getLocalConversionId(boardId as string);
            dispatch(setCurrentTaskFrameIds(null))
            executeTask(conversionId as string, [task], psId)
            setTimeout(()=>{
                hasExecuted.current = false;
            }, 1500)
        }, 200),
        [boardId]
    );

    useEffect(() => {
        return () => {
            hasExecuted.current = false;
        };
    }, []);

    const cancelTask = async (task: AnalysisTaskSubProcess) => {
        const cancelToolName = task.toolName;
        const extra = { cancelTask: [cancelToolName]};
        updateStatus(taskId, TaskStatusType.CANCELED, extra);
        updateMainContainerStatus(TaskStatusType.CANCELED, cancelToolName as CommandType, taskId)
        taskId && changeTaskProcess(taskId, {extra});
    }

    const excuseTemplate = useCallback((task: AnalysisTaskSubProcess, index: number) => {
        const conversionItem = status ? JSON.parse(status) : null;
        const toolName = task.toolName;
        const stepStatus = conversionItem?.stepStatus[toolName] as TaskStatusType;
        const selfStatus = conversionItem.selfStatus;

        // 抽取按钮模板
        const excuseButtonTemplate = (
            <Box style={{marginTop: centerChat ? 0 : 8, marginRight: centerChat ? 8: 0, textAlign: "right", minWidth: 150}}>
                <Button variant="outlined" size='small' style={{height: '25px'}} onClick={async ()=> await cancelTask(task)}>{t('cancel')}</Button>
                <Button
                    variant="contained"
                    size='small'
                    style={{marginLeft: 10, height: '25px'}}
                    onClick={() => {
                        editor?.selectNone();
                        dispatch(setScreenPosition(null))
                        excuseTask(task, parentStepId)}
                    }
                >
                    {t('confirm_execute')}
                </Button>
            </Box>
        );

        // 状态标签映射
        const statusLabels: Record<TaskStatusType, string> = {
            [TaskStatusType.PENDINGEXECUTION]: t('pending'),
            [TaskStatusType.CONFIRMING]: t('waiting_confirm'),
            [TaskStatusType.COMPLETED]: t('confirmed'),
            [TaskStatusType.CANCELED]: t('cancelled'),
            [TaskStatusType.EXECUTING]: t('executing'),
            [TaskStatusType.PENDING]: t('waiting'),
            [TaskStatusType.EXCEPTION]: t('task_failed'),
        };

        // 获取状态标签组件
        const getStatusLabel = (status: TaskStatusType) => (
            <>
                <span className='status_label' style={{paddingTop: centerChat ? 0 : 8, marginRight: centerChat ? 8: 0, textAlign: "right", minWidth: 50}}>{statusLabels[status]} {status === TaskStatusType.COMPLETED && <CheckOutlinedIcon style={{fontSize: 16}} />}</span>
            </>
        );

        // 判断是否显示按钮
        const shouldShowButton = () => {
            if (!stepStatus && index === 0) return true;

            if (index > 0) {
                const currentTask = plan.data[index -1];
                const currentTaskStatus = conversionItem?.stepStatus[currentTask.toolName]
                if (currentTaskStatus === TaskStatusType.COMPLETED) return true
            }

            return false;
        };

        if (extra?.cancelTask?.includes(task.toolName)){ // 取消任务
            return {template: getStatusLabel(TaskStatusType.CANCELED), status: TaskStatusType.CANCELED};
        }

        if (stepStatus && statusLabels[stepStatus]) {
            return {template: getStatusLabel(stepStatus), status: stepStatus}
        }

        if (shouldShowButton()) {
          //  centerChat && excuseTask(task, parentStepId);
            return {template: excuseButtonTemplate, status: TaskStatusType.PENDINGEXECUTION};
        }

        return null;
    }, [taskProcess, status, taskListProcess, plan, parentStepId]);


    const stepHighLight = useCallback((toolName: string, index: number)=>{
        let highlight = false;
        const conversionItem = status ? JSON.parse(status) : null;
        const stepStatus = conversionItem?.stepStatus[toolName] as TaskStatusType;
        const selfStatus = conversionItem?.selfStatus;

        const shouldShowButton = () => {
            if (!stepStatus && index === 0) return true;

            if (index > 0) {
                const currentTask = plan.data[index -1];
                const currentTaskStatus = conversionItem?.stepStatus[currentTask.toolName]
                if (currentTaskStatus === TaskStatusType.COMPLETED) return true
            }

            return false;
        };

        if ((stepStatus && stepStatus !== TaskStatusType.COMPLETED) || shouldShowButton()) highlight = true

        if (stepStatus === TaskStatusType.COMPLETED || selfStatus === TaskStatusType.CANCELED) highlight = false

        return highlight;
    }, [status])

    const statusIcon = (status?: TaskStatusType) => {
        switch (status) {
            case TaskStatusType.COMPLETED:
                return {icon: <CheckIcon style={{fontSize: 16, color: "var(--c-accent)"}} />, color: 'var(--c-success-bg)'}
            case TaskStatusType.PENDINGEXECUTION:
                return {icon: <PanoramaFishEyeIcon style={{fontSize: 16, color: "var(--c-accent)"}} />, color: 'var(--c-warning-bg)'}
            case TaskStatusType.EXECUTING:
                return {icon: <TaskLoading  size={13}/>, color: "var(--c-accent-bg)"}
            default:
                return {icon: <PanoramaFishEyeIcon style={{fontSize: 16}} />, color: 'var(--c-border-light)'}
        }
    }

    return <>
        {
            plan?.data?.map((task, index) => {
                const templateStatus = excuseTemplate(task, index);
                const iconStatus = statusIcon(templateStatus?.status);
                const currentHeightLight = stepHighLight(task.toolName, index);
                if (currentHeightLight) {
                    const currentTask = {
                        icon: templateStatus?.status,
                        color: iconStatus?. color,
                        template: templateStatus?.template,
                        task,
                        index: index + 1,
                        content,
                        status,
                        count: plan?.data?.length || 0
                    }
                    currentTaskCache && currentTaskCache(currentTask)
                }
                return <Fragment key={index}>
                    {
                        centerChat ?
                            <div className='step'
                                 style={{backgroundColor: iconStatus.color}}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    &nbsp;&nbsp;{iconStatus.icon}&nbsp;
                                    <span style={{ maxWidth: "700px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {task.note}
                              </span>
                                </div>
                                <div>{ conversionValue === ConversionType.CONVERSING && templateStatus?.template }</div>
                            </div> : <Box className="task_manage_sub" style={{color: currentHeightLight ? 'var(--c-text-primary)' : 'var(--c-text-secondary)'}}>{task.note}</Box>
                    }
                    { conversionValue === ConversionType.CONVERSING && !centerChat && templateStatus?.template }
                </Fragment>
            })
        }
    </>
});

interface PromptProps {
    loading?: boolean,
    taskResponse?: ResponseProp,
    content?: string
    status?: TaskStatusType,
    taskId?: number,
    extra?: ExtraProps
}

export { TaskPlan };

export default function Analysis (props: PromptProps) {

    const {loading, taskResponse, content, status, taskId, extra} = props;

    return <Box className="task_manage_box">
        {content}
        <TaskPlan plan={taskResponse as analysisTaskProcess} status={status} extra={extra} taskId={taskId} content={content} />
    </Box>
}